// src/routes/QuestionModal.js
import React from "react";
import { apiRequest } from "../api";

const inputStyle = {
  width: "100%",
  minWidth: 0,
  padding: "10px 12px",
  background: "#f5f6f8",
  border: "none",
  borderRadius: "6px",
  fontSize: "0.95rem",
  boxSizing: "border-box",
};

const numberInputStyle = {
  ...inputStyle,
  textAlign: "center",
};

const labelStyle = {
  color: "#2563eb",
  fontWeight: 600,
};

const responseOptionHeaderTextStyle = {
  padding: "0 12px",
};

const normalizeQuestionTypeDescription = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const isSignatureAgreementQuestionType = (questionTypeId, description) =>
  Number(questionTypeId) === 33 ||
  normalizeQuestionTypeDescription(description) === "signature_agreement";

const isMultipleSelectQuestionType = (questionTypeId, description) =>
  Number(questionTypeId) === 28 ||
  normalizeQuestionTypeDescription(description) === "multiple_select";

const normalizeChoiceCategoryValue = (value) => String(value ?? "").trim();

const resolveSymptomCategoryId = (value, symptomCategories) => {
  const normalizedValue = normalizeChoiceCategoryValue(value);

  if (!normalizedValue) {
    return "";
  }

  const exactIdMatch = symptomCategories.find(
    (categoryOption) =>
      String(categoryOption?.symptom_category_id ?? "") === normalizedValue
  );

  if (exactIdMatch) {
    return String(exactIdMatch.symptom_category_id);
  }

  const normalizedLabel = normalizedValue.toLowerCase();
  const labelMatch = symptomCategories.find(
    (categoryOption) =>
      String(categoryOption?.category ?? "").trim().toLowerCase() === normalizedLabel
  );

  return labelMatch ? String(labelMatch.symptom_category_id) : "";
};

const normalizeChoiceForEditing = (choice = {}, fallbackOrder = 0) => ({
  ...choice,
  option: String(choice?.option ?? ""),
  option_more_info: String(choice?.option_more_info ?? ""),
  category: normalizeChoiceCategoryValue(choice?.category),
  report_verbiage: String(choice?.report_verbiage ?? ""),
  value: Number(choice?.value ?? 0),
  order: Number(choice?.order ?? fallbackOrder),
});


export default function QuestionModal({
  isOpen,
  isCreateMode,
  editingQuestion,
  setEditingQuestion,
  questionTypes,
  conditionalSections,
  selectedQuestionType,
  saving,
  onCancel,
  onSave,
}) {
  const optionInputRefs = React.useRef({});
  const nextOptionFocusIndexRef = React.useRef(null);
  const [symptomCategories, setSymptomCategories] = React.useState([]);
  const choicesLength = editingQuestion?.choices?.length ?? 0;

  const isMultipleSelect = isMultipleSelectQuestionType(
    editingQuestion?.question_type_id,
    selectedQuestionType?.description
  );

  React.useEffect(() => {
    if (!isOpen || !isMultipleSelect) return;

    let isCancelled = false;

    const loadSymptomCategories = async () => {
      try {
        const res = await apiRequest("/api/symptom-categories");
        const data = res.ok ? await res.json() : [];

        if (!isCancelled) {
          setSymptomCategories(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!isCancelled) {
          setSymptomCategories([]);
        }
      }
    };

    loadSymptomCategories();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, isMultipleSelect]);

  React.useEffect(() => {
    if (!isOpen || !isMultipleSelect || !editingQuestion || !symptomCategories.length) {
      return;
    }

    const choices = Array.isArray(editingQuestion.choices) ? editingQuestion.choices : [];
    let hasChanges = false;

    const nextChoices = choices.map((choice) => {
      const currentCategory = normalizeChoiceCategoryValue(choice?.category);
      const nextCategory = resolveSymptomCategoryId(currentCategory, symptomCategories);

      if (currentCategory === nextCategory) {
        return choice;
      }

      hasChanges = true;
      return {
        ...choice,
        category: nextCategory,
      };
    });

    if (hasChanges) {
      setEditingQuestion({
        ...editingQuestion,
        choices: nextChoices,
      });
    }
  }, [
    editingQuestion,
    isMultipleSelect,
    isOpen,
    setEditingQuestion,
    symptomCategories,
  ]);

  React.useEffect(() => {
    if (!isOpen || !editingQuestion) return;

    const nextIndex = nextOptionFocusIndexRef.current;
    if (nextIndex === null || nextIndex === undefined) return;

    const input = optionInputRefs.current[nextIndex];
    if (input) {
      input.focus();
      input.select?.();
    }

    nextOptionFocusIndexRef.current = null;
  }, [isOpen, editingQuestion, choicesLength]);

  if (!isOpen || !editingQuestion) return null;
//   console.log("editingQuestion", editingQuestion )
//   console.log("is create mode", isCreateMode)

  const isPerformTaskVideo =
    normalizeQuestionTypeDescription(selectedQuestionType?.description) ===
    "perform_task_video";
  const isSignatureAgreement = isSignatureAgreementQuestionType(
    editingQuestion.question_type_id,
    selectedQuestionType?.description
  );
  const normalizedChoices = Array.isArray(editingQuestion.choices)
    ? editingQuestion.choices.map((choice, index) => normalizeChoiceForEditing(choice, index + 1))
    : [];
  const signatureAgreementChoicesJson =
    typeof editingQuestion.choices_json === "string"
      ? editingQuestion.choices_json
      : JSON.stringify(editingQuestion.choices ?? [], null, 2);

  const sortedChoices = [...normalizedChoices].sort(
        (a, b) => a.order - b.order
    );
  const longestOptionLength = sortedChoices.reduce(
    (maxLength, choice) => Math.max(maxLength, String(choice?.option ?? "").length),
    "Option".length
  );
  const optionColumnWidth = `${Math.max(Math.round((longestOptionLength + 2) * 0.85), 10)}ch`;
  const responseOptionGridTemplateColumns = isMultipleSelect
    ? `${optionColumnWidth} minmax(253px, 1.15fr) minmax(220px, 1fr) 90px 90px 40px`
    : "3fr 1fr 90px 90px 40px";

  const conditionalResponseOptions = sortedChoices
    .map((choice, idx) => {
      const optionLabel = String(choice?.option ?? "").trim();
      if (!optionLabel) return null;
      return {
        key: `${optionLabel}-${idx}`,
        label: optionLabel,
      };
    })
    .filter(Boolean);

  const isConditionalResponseEnabled = Boolean(
    editingQuestion.conditional_response_enabled
  );
  const conditionalOptionToDisplay =
    editingQuestion.conditional_response_option ?? "";
  const conditionalValue =
    editingQuestion.conditional_response_value === undefined ||
    editingQuestion.conditional_response_value === null
      ? ""
      : String(editingQuestion.conditional_response_value);
  const hideConditionalResponseControls = Boolean(
    editingQuestion.hide_conditional_response_controls
  );
  const conditionalTargetSectionId =
    editingQuestion.target_section_id === undefined ||
    editingQuestion.target_section_id === null
      ? ""
      : String(editingQuestion.target_section_id);
  const subQuestionTypeId =
    editingQuestion.sub_question_type === undefined ||
    editingQuestion.sub_question_type === null
      ? ""
      : String(editingQuestion.sub_question_type);
  const subQuestionPrompt =
    editingQuestion.sub_question_prompt === undefined ||
    editingQuestion.sub_question_prompt === null
      ? ""
      : String(editingQuestion.sub_question_prompt);
    // console.log("sorted choices", sortedChoices)

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.45)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        padding: "24px",
      }}
    >
      <div
        style={{
          background: "#fff",
          width: "900px",
          maxHeight: "90vh",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #eee",
            fontWeight: 600,
            fontSize: "1.1rem",
          }}
        >
          {isCreateMode
            ? hideConditionalResponseControls
              ? "Add Question - Conditional Flow"
              : "Add Question"
            : `Edit Question (Question ID: ${editingQuestion.question_id}, Question Section ID: ${editingQuestion.question_section_id})`}
        </div>

        {/* Body */}
        <div style={{ padding: "24px", overflowY: "auto" }}>
          {/* Title */}
          <div style={{ marginBottom: "16px" }}>
            <label style={labelStyle}>Title</label>
            <input
              style={inputStyle}
              value={editingQuestion.title}
              onChange={(e) =>
                setEditingQuestion({ ...editingQuestion, title: e.target.value })
              }
            />
          </div>

          {/* Question */}
          <div style={{ marginBottom: "16px" }}>
            <label style={labelStyle}>Question Verbiage</label>
            <textarea
              rows={3}
              style={inputStyle}
              value={editingQuestion.question}
              onChange={(e) =>
                setEditingQuestion({
                  ...editingQuestion,
                  question: e.target.value,
                })
              }
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={labelStyle}>Question Order</label>
            <input
              type="number"
              min={0}
              max={999}
              step={1}
              style={{ ...numberInputStyle, width: "96px" }}
              value={
                editingQuestion.question_order === undefined ||
                editingQuestion.question_order === null
                  ? ""
                  : editingQuestion.question_order
              }
              onChange={(e) => {
                const rawValue = e.target.value;
                const parsed = Number(rawValue);

                setEditingQuestion({
                  ...editingQuestion,
                  question_order:
                    rawValue === "" || !Number.isFinite(parsed) || parsed <= 0
                      ? undefined
                      : Math.min(999, Math.floor(parsed)),
                });
              }}
            />
          </div>

          <div style={{ marginBottom: "16px", display: "flex", gap: "20px", flexWrap: "wrap" }}>
            <label style={{ ...labelStyle, display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={Boolean(editingQuestion.is_required)}
                onChange={(e) =>
                  setEditingQuestion({
                    ...editingQuestion,
                    is_required: e.target.checked,
                  })
                }
              />
              Required
            </label>

            <label style={{ ...labelStyle, display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={Boolean(editingQuestion.include_sum_total)}
                onChange={(e) =>
                  setEditingQuestion({
                    ...editingQuestion,
                    include_sum_total: e.target.checked,
                  })
                }
              />
              Include in Total Score
            </label>

            <label style={{ ...labelStyle, display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={Boolean(editingQuestion.unique_calculation)}
                onChange={(e) =>
                  setEditingQuestion({
                    ...editingQuestion,
                    unique_calculation: e.target.checked,
                  })
                }
              />
              Unique Calculation
            </label>

            <label style={{ ...labelStyle, display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={Boolean(editingQuestion.has_subquestion)}
                onChange={(e) =>
                  setEditingQuestion({
                    ...editingQuestion,
                    has_subquestion: e.target.checked,
                    sub_question_type: e.target.checked
                      ? editingQuestion.sub_question_type ?? ""
                      : "",
                  })
                }
              />
              Has Subquestion
            </label>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={labelStyle}>Subquestion Type</label>
            <select
              style={{
                ...inputStyle,
                opacity: editingQuestion.has_subquestion ? 1 : 0.6,
                cursor: editingQuestion.has_subquestion ? "pointer" : "not-allowed",
              }}
              value={subQuestionTypeId}
              disabled={!editingQuestion.has_subquestion}
              onChange={(e) =>
                setEditingQuestion({
                  ...editingQuestion,
                  sub_question_type: e.target.value ? Number(e.target.value) : "",
                })
              }
            >
              <option value="">Select subquestion type</option>
              {questionTypes.map((qt) => (
                <option key={`sub-question-type-${qt.question_type_id}`} value={qt.question_type_id}>
                  {qt.description.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={labelStyle}>Subquestion Prompt</label>
            <input
              style={{
                ...inputStyle,
                opacity: editingQuestion.has_subquestion ? 1 : 0.6,
                cursor: editingQuestion.has_subquestion ? "text" : "not-allowed",
              }}
              value={subQuestionPrompt}
              disabled={!editingQuestion.has_subquestion}
              onChange={(e) =>
                setEditingQuestion({
                  ...editingQuestion,
                  sub_question_prompt: e.target.value,
                })
              }
            />
          </div>

          {/* Question Type */}
          <div style={{ marginBottom: "16px" }}>
            <label style={labelStyle}>Question Response Types</label>
            <select
              style={inputStyle}
              value={editingQuestion.question_type_id}
              onChange={(e) => {
                const newTypeId = Number(e.target.value);
                const qt = questionTypes.find(
                    (t) => t.question_type_id === newTypeId
                );
                const isNextSignatureAgreement = isSignatureAgreementQuestionType(
                  newTypeId,
                  qt?.description
                );

                const options = qt?.options;
                const nextChoices = Array.isArray(options)
                  ? options.map((opt, idx) => normalizeChoiceForEditing(opt, idx + 1))
                  : options && typeof options === "object"
                    ? Object.entries(options).map(([option, value], idx) =>
                        normalizeChoiceForEditing(
                          {
                            option,
                            option_more_info: "",
                            report_verbiage: "",
                            value,
                            order: idx + 1,
                          },
                          idx + 1
                        )
                      )
                    : [];

                setEditingQuestion({
                    ...editingQuestion,
                    question_type_id: newTypeId,
                    choices: isNextSignatureAgreement ? editingQuestion.choices : nextChoices,
                    choices_json: isNextSignatureAgreement
                      ? typeof editingQuestion.choices_json === "string"
                        ? editingQuestion.choices_json
                        : JSON.stringify(editingQuestion.choices ?? [], null, 2)
                      : "",
                });
            }}

            >
              {questionTypes.map((qt) => (
                <option key={qt.question_type_id} value={qt.question_type_id}>
                  {qt.description.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>

          {(isPerformTaskVideo || isSignatureAgreement) && (
            <div style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>
                {isSignatureAgreement ? "Document URL (PDF)" : "Hyperlink"}
              </label>
              <input
                style={inputStyle}
                placeholder={
                  isSignatureAgreement
                    ? "https://.../agreement.pdf"
                    : "https://..."
                }
                value={editingQuestion.hyperlink ?? ""}
                onChange={(e) =>
                  setEditingQuestion({
                    ...editingQuestion,
                    hyperlink: e.target.value,
                  })
                }
              />
            </div>
          )}

          {/* Default Options Toggle */}
          {/* {selectedQuestionType?.options && (
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "flex", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={editingQuestion.use_default_options}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEditingQuestion({
                      ...editingQuestion,
                      use_default_options: checked,
                      choices: checked
                        ? Object.entries(selectedQuestionType.options).map(
                            ([option, value], idx) => ({
                              option,
                              value,
                              order: idx + 1,
                            })
                          )
                        : [],
                    });
                  }}
                />
                Use default response options
              </label>
            </div>
          )} */}

          {/* Choices */}
          <div>
            <div
              style={{
                fontWeight: 600,
                marginBottom: "8px",
                color: "#2563eb",
              }}
            >
              {isSignatureAgreement ? "Response JSON" : "Response Options"}
            </div>

            {isSignatureAgreement ? (
              <textarea
                rows={12}
                style={{
                  ...inputStyle,
                  fontFamily: "Consolas, 'Courier New', monospace",
                  whiteSpace: "pre",
                  resize: "vertical",
                }}
                value={signatureAgreementChoicesJson}
                onChange={(e) =>
                  setEditingQuestion({
                    ...editingQuestion,
                    choices_json: e.target.value,
                  })
                }
              />
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: responseOptionGridTemplateColumns,
                    columnGap: "8px",
                    rowGap: "8px",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      ...responseOptionHeaderTextStyle,
                      fontSize: "0.8rem",
                      color: "#666",
                      paddingTop: "6px",
                      paddingBottom: "6px",
                    }}
                  >
                    Option
                  </div>
                  <div
                    style={{
                      ...responseOptionHeaderTextStyle,
                      fontSize: "0.8rem",
                      color: "#666",
                      paddingTop: "6px",
                      paddingBottom: "6px",
                    }}
                  >
                    Option More Info
                  </div>
                  {isMultipleSelect && (
                    <div
                      style={{
                        ...responseOptionHeaderTextStyle,
                        fontSize: "0.8rem",
                        color: "#666",
                        paddingTop: "6px",
                        paddingBottom: "6px",
                      }}
                    >
                      Category
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "#666",
                      textAlign: "center",
                      paddingTop: "6px",
                      paddingBottom: "6px",
                    }}
                  >
                    Value
                  </div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "#666",
                      textAlign: "center",
                      paddingTop: "6px",
                      paddingBottom: "6px",
                    }}
                  >
                    Order
                  </div>
                  <div />

                  {sortedChoices.map((r, idx) => {
                    const originalIndex = normalizedChoices.indexOf(r);

                    return (
                      <React.Fragment key={`choice-${originalIndex}-${idx}`}>
                        <input
                          ref={(el) => {
                          if (el) {
                            optionInputRefs.current[originalIndex] = el;
                          } else {
                            delete optionInputRefs.current[originalIndex];
                          }
                          }}
                            style={inputStyle}
                            value={r.option}
                            onChange={(e) => {
                            const next = [...normalizedChoices];
                            next[originalIndex] = {
                                ...next[originalIndex],
                                option: e.target.value,
                            };
                            setEditingQuestion({ ...editingQuestion, choices: next });
                            }}
                        />


                        <input
                          style={inputStyle}
                          value={r.option_more_info ?? ""}
                          onChange={(e) => {
                          const next = [...normalizedChoices];
                          next[originalIndex] = {
                            ...next[originalIndex],
                            option_more_info: e.target.value,
                          };
                          setEditingQuestion({ ...editingQuestion, choices: next });
                          }}
                        />

                        {isMultipleSelect && (
                          <select
                            style={inputStyle}
                            value={resolveSymptomCategoryId(r.category, symptomCategories)}
                            onChange={(e) => {
                              const next = [...normalizedChoices];
                              next[originalIndex] = {
                                ...next[originalIndex],
                                category: e.target.value,
                              };
                              setEditingQuestion({ ...editingQuestion, choices: next });
                            }}
                          >
                            <option value="">None</option>
                            {symptomCategories.map((categoryOption) => {
                              const categoryValue = String(
                                categoryOption?.symptom_category_id ?? ""
                              );
                              const categoryLabel = String(categoryOption?.category ?? "");

                              return (
                                <option
                                  key={categoryOption?.symptom_category_id ?? categoryValue}
                                  value={categoryValue}
                                >
                                  {categoryLabel}
                                </option>
                              );
                            })}
                          </select>
                        )}


                        <input
                            type="number"
                            style={numberInputStyle}
                            value={r.value}
                            onChange={(e) => {
                          const next = [...normalizedChoices];
                            next[originalIndex] = {
                                ...next[originalIndex],
                                value: Number(e.target.value),
                            };
                            setEditingQuestion({ ...editingQuestion, choices: next });
                            }}
                        />

                        <input
                            type="number"
                            style={numberInputStyle}
                            value={r.order}
                            onChange={(e) => {
                          const next = [...normalizedChoices];
                            next[originalIndex] = {
                                ...next[originalIndex],
                                order: Number(e.target.value),
                            };
                            setEditingQuestion({ ...editingQuestion, choices: next });
                            }}
                        />

                        <button
                          onClick={() =>
                          setEditingQuestion({
                            ...editingQuestion,
                            choices: normalizedChoices.filter(
                            (_, i) => i !== originalIndex
                            ),
                          })
                          }
                          style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          }}
                        >
                          ❌
                        </button>
                        </React.Fragment>
                    );
                      })}
                    </div>

                {!editingQuestion.use_default_options && (
                  <button
                    style={{
                        marginTop: "8px",
                        background: "transparent",
                        border: "none",
                        color: "#2563eb",
                        cursor: "pointer",
                    }}
                    onClick={() =>
                      {
                      const nextIndex = normalizedChoices.length;
                      nextOptionFocusIndexRef.current = nextIndex;

                        setEditingQuestion({
                        ...editingQuestion,
                        choices: [
                            ...normalizedChoices,
                            normalizeChoiceForEditing(
                              {
                                option: "",
                                option_more_info: "",
                                category: "",
                                report_verbiage: "",
                                value: 0,
                                order: normalizedChoices.length + 1,
                              },
                              normalizedChoices.length + 1
                            ),
                        ],
                        })
                          }
                    }
                    >
                    + Add Response
                </button>

                )}
              </>
            )}
          </div>

          {/* Active */}
          <div style={{ marginTop: "24px" }}>
            <label style={{ ...labelStyle, display: "flex", gap: "8px" }}>
              <input
                type="checkbox"
                checked={editingQuestion.is_active}
                onChange={(e) =>
                  setEditingQuestion({
                    ...editingQuestion,
                    is_active: e.target.checked,
                  })
                }
              />
              Active
            </label>
          </div>

          {!hideConditionalResponseControls && (
            <div style={{ marginTop: "16px" }}>
              <label style={{ ...labelStyle, display: "flex", gap: "8px", alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={isConditionalResponseEnabled}
                  onChange={(e) =>
                    setEditingQuestion({
                      ...editingQuestion,
                      conditional_response_enabled: e.target.checked,
                    })
                  }
                />
                Conditional Response
              </label>
            </div>
          )}

          {!hideConditionalResponseControls && isConditionalResponseEnabled && (
            <div
              style={{
                marginTop: "12px",
                display: "grid",
                gridTemplateColumns: "1fr 220px 220px",
                gap: "12px",
                alignItems: "end",
              }}
            >
              <div>
                <label style={labelStyle}>Target Section</label>
                <select
                  style={inputStyle}
                  value={conditionalTargetSectionId}
                  onChange={(e) =>
                    setEditingQuestion({
                      ...editingQuestion,
                      target_section_id: e.target.value ? Number(e.target.value) : "",
                    })
                  }
                >
                  <option value="">Select target section</option>
                  {(conditionalSections || []).map((section) => (
                    <option key={section.section_id} value={section.section_id}>
                      {section.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Response Option</label>
                <select
                  style={inputStyle}
                  value={conditionalOptionToDisplay}
                  onChange={(e) =>
                    setEditingQuestion({
                      ...editingQuestion,
                      conditional_response_option: e.target.value,
                    })
                  }
                >
                  <option value="">Select response option</option>
                  {conditionalResponseOptions.map((option) => (
                    <option key={option.key} value={option.label}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Trigger Value</label>
                <input
                  style={inputStyle}
                  value={conditionalValue}
                  onChange={(e) =>
                    setEditingQuestion({
                      ...editingQuestion,
                      conditional_response_value: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #eee",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <button onClick={onCancel}>Cancel</button>
          <button
            disabled={saving}
            onClick={() =>
              onSave({
                isConditionalResponseEnabled,
                conditionalTargetSectionId,
                conditionalOptionToDisplay,
                conditionalValue,
              })
            }
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
